import eq from "lodash-es/eq"

import { EUserRole } from "@/3514/types"
import { buildCssClassName, getTransitionTwClasses } from "@/3514/utils"

import { ESessionInfoColorSchema as ESchema } from "./session-info.types"

export default {
    container: ({ viewerRole }: { viewerRole: EUserRole }): string =>
        buildCssClassName("[display:grid] gap-[15px] w-full", eq(viewerRole, EUserRole.Coach) && "md:max-w-[800px]"),
    header: {
        container: ({
            isExpanded,
            isCancelled,
            isDateless
        }: {
            isExpanded: boolean
            isCancelled: boolean
            isDateless: boolean
        }): string =>
            buildCssClassName(
                "py-[15px]",
                "px-[10px]",
                "md:px-[20px]",
                "gap-x-[10px]",
                "md:gap-x-[25px]",
                "flex",
                "justify-start",
                "items-center",
                "w-full",
                "h-[75px]",
                { "md:max-w-fit": isDateless },
                { "rounded-tl-[10px] rounded-tr-[10px] rounded-bl-[0px] rounded-br-[0px]": isExpanded },
                { "rounded-[10px]": !isExpanded },
                { "opacity-50": isCancelled },
                getTransitionTwClasses("all", 200)
            ),
        image: (): string => "rounded-full bg-white flex items-center justify-center w-[45px] h-[45px]",
        col: (): string => "flex flex-col md:flex-row items-start md:items-center md:gap-x-[5px]",
        dateWrapper: (): string => "w-max"
    },
    timeRemark: {
        container: (): string => "self-center w-full max-w-[480px]"
    },
    body: {
        container: ({
            colorSchema,
            isAccordionExpanded,
            isAccordionExpandable
        }: {
            colorSchema: ESchema
            isAccordionExpanded: boolean
            isAccordionExpandable: boolean
        }): string =>
            buildCssClassName(
                "w-full",
                "flex",
                "flex-col",
                "pb-[20px]",
                "rounded-tl-[0px]",
                "rounded-tr-[0px]",
                "rounded-br-[10px]",
                "rounded-bl-[10px]",
                "border-[1px]",
                "!outline-none",
                isAccordionExpanded
                    ? [
                          { "opacity-[1]": true },
                          isAccordionExpandable && [
                              { "border-t-transparent": true },
                              colorSchema === ESchema.BlueBorderWhiteBg && [
                                  "group-focus:border-r-blue-300",
                                  "group-focus:border-l-blue-300",
                                  "group-focus:border-b-blue-300",
                                  "border-r-blue-400",
                                  "border-l-blue-400",
                                  "border-b-blue-400"
                              ],
                              colorSchema === ESchema.TransparentBorderBlueBg && [
                                  "group-focus:border-r-blue-500",
                                  "group-focus:border-l-blue-500",
                                  "group-focus:border-b-blue-500",
                                  "border-r-blue-500",
                                  "border-l-blue-500",
                                  "border-b-blue-500"
                              ],
                              colorSchema === ESchema.AccentBorderWhiteBg && [
                                  "group-focus:border-r-accent",
                                  "group-focus:border-l-accent",
                                  "group-focus:border-b-accent",
                                  "border-r-accent",
                                  "border-l-accent",
                                  "border-b-accent"
                              ],
                              colorSchema === ESchema.OrangeBorderWhiteBg && [
                                  "group-focus:border-r-red-200",
                                  "group-focus:border-l-red-200",
                                  "group-focus:border-b-red-200",
                                  "border-r-red-300",
                                  "border-l-red-300",
                                  "border-b-red-300"
                              ]
                          ]
                      ]
                    : { "border-transparent": true, "opacity-[0]": true },
                getTransitionTwClasses("all", 300, "ease-in-out")
            ),
        messages: {
            container: (): string => "flex flex-col",
            item: ({ isAccessible }: { isAccessible: boolean }): string =>
                buildCssClassName(
                    "py-[8px]",
                    "px-[20px]",
                    "flex",
                    "items-center",
                    "justify-start",
                    "gap-x-[5px]",
                    "border-[1px]",
                    "border-t-transparent",
                    "border-r-transparent",
                    "border-l-transparent",
                    "border-b-blue-500",
                    "bg-blue-600",
                    [
                        isAccessible && [
                            "cursor-pointer",
                            "hover:bg-blue-400",
                            "hover:border-blue-300",
                            "focus-visible:bg-blue-400",
                            "focus-visible:border-blue-300",
                            getTransitionTwClasses("all", 200)
                        ]
                    ]
                )
        },
        list: {
            container: ({ length }: { length: number }): string =>
                buildCssClassName(
                    "py-[10px]",
                    "px-[20px]",
                    "gap-y-[5px]",
                    "gap-x-[10px]",
                    "max-h-[280px]",
                    "md:max-h-[290px]",
                    "overflow-y-scroll",
                    "no-scrollbar",
                    "[display:grid]",
                    [eq(length, 1) ? "grid-cols-1" : "grid-cols-2"],
                    "md:flex",
                    "md:flex-wrap"
                ),
            item: ({ role }: { role: EUserRole }): string =>
                buildCssClassName(
                    "flex",
                    "flex-col",
                    "basis-[45%]",
                    "md:basis-1/6",
                    "min-w-[120px]",
                    "max-w-[200px]",
                    "grow",
                    "w-full",
                    "[margin:0_auto]",
                    "items-center",
                    "gap-y-[5px]",
                    "p-[10px]",
                    "md:p-[15px]",
                    "bg-white",
                    "rounded-[10px]",
                    "hover:bg-blue-600",
                    "focus-visible:bg-blue-600",
                    [eq(role, EUserRole.Coach) ? "cursor-pointer" : "cursor-default"],
                    getTransitionTwClasses("colors", 100)
                )
        },
        bottom: (): string =>
            buildCssClassName(
                "flex",
                "flex-col",
                "gap-y-[15px]",
                "pt-[20px]",
                "px-[30px]",
                "pb-[15px]",
                "w-[calc(100%-20px)]",
                "self-center",
                "[box-shadow:white_0px_0px_30px_5px]",
                "relative",
                "before:absolute",
                "before:w-full",
                "before:h-[20px]",
                "before:bg-white/10",
                "before:top-[-20px]",
                "before:inset-0"
            ),
        buttons: (): string => "flex flex-col lg:flex-row justify-center items-center gap-y-[10px] md:gap-x-[20px]"
    },
    headerAvatar: {
        wrapper: (): string => "flex relative items-center justify-center bg-blue-500 rounded-full",
        giftIcon: (): string => "absolute right-[-3px] top-[0px]"
    },
    arrowIcon: {
        container: (): string =>
            buildCssClassName(
                "flex",
                "items-center",
                "justify-center",
                "w-[35px]",
                "h-[35px]",
                "!outline-none",
                "cursor-pointer",
                "rounded-full",
                "bg-transparent",
                "hover:bg-blue-500",
                "focus-visible:bg-blue-500",
                getTransitionTwClasses("all", 200)
            ),
        arrow: (): string => getTransitionTwClasses("all", 200)
    },
    attendee: {
        container: ({ isAccessible }: { isAccessible: boolean }): string =>
            buildCssClassName(
                "py-[12px]",
                "px-[15px]",
                "bg-white",
                "flex",
                "flex-col",
                "gap-y-[5px]",
                "items-center",
                "basis-[45%]",
                "md:basis-1/6",
                "min-w-[120px]",
                "max-w-[120px]",
                "md:max-w-[200px]",
                "grow",
                "w-full",
                "[margin:0_auto]",
                "rounded-[10px]",
                { "hover:bg-blue-600 focus-visible:bg-blue-600 cursor-pointer !outline-none": isAccessible },
                { "cursor-default": !isAccessible },
                [isAccessible && getTransitionTwClasses("all", 200)]
            ),
        name: (): string => "max-w-[65px] truncate"
    },
    avatarAttendanceIcon: {
        container: ({ isPresent }: { isPresent: boolean }): string =>
            buildCssClassName(
                "w-[19px]",
                "h-[19px]",
                "absolute",
                "right-[-5px]",
                "top-[-2px]",
                "flex",
                "items-center",
                "justify-center",
                "rounded-full",
                "border-[1px]",
                "border-white",
                { "bg-green-200": isPresent },
                { "bg-red-200": !isPresent }
            )
    },
    giftIcon: {
        container: (): string =>
            buildCssClassName([
                "w-[17px]",
                "h-[17px]",
                "bg-blue-200",
                "border-[1px]",
                "border-white",
                "rounded-full",
                "flex",
                "items-center",
                "justify-center"
            ]),
        svg: (): string => "scale-[0.9]"
    },
    cancelledSessionModalOpener: {
        button: (): string =>
            buildCssClassName(
                "flex",
                "items-center",
                "gap-x-[5px]",
                "cursor-pointer",
                "z-[1]",
                "border-b-[1px]",
                "border-b-transparent",
                "hover:scale-[1.075]",
                "focus-visible:scale-[1.075]",
                "focus-visible:border-b-blue-200",
                "!outline-none",
                "z-[0]",
                "after:absolute",
                "after:z-[-1]",
                "after:inset-[-5px]",
                getTransitionTwClasses("all", 200)
            )
    },
    postSessionModalOpener: {
        button: ({ isDisabled }: { isDisabled: boolean }): string =>
            buildCssClassName(
                "rounded-[30px]",
                "flex",
                "items-center",
                "justify-center",
                "md:gap-x-[5px]",
                "relative",
                "h-[25px]",
                "px-[10px]",
                "z-[0]",
                "after:absolute",
                "after:z-[-1]",
                "after:inset-[-7px]",
                "!outline-none",
                { "bg-gray-300": isDisabled },
                { "bg-blue-200 hover:bg-blue-100 focus:bg-blue-100": !isDisabled },
                getTransitionTwClasses("all", 200)
            ),
        label: (): string => buildCssClassName("w-max", getTransitionTwClasses("all", 150))
    },
    attendanceModalOpener: (): string =>
        buildCssClassName(
            "rounded-[30px]",
            "px-[10px]",
            "flex",
            "items-center",
            "justify-center",
            "md:gap-x-[5px]",
            "relative",
            "h-[25px]",
            "z-[0]",
            "after:absolute",
            "after:z-[-1]",
            "after:inset-[-7px]",
            "!outline-none",
            "border-[1px]",
            "bg-white",
            "hover:bg-blue-600",
            "focus:bg-blue-600",
            "border-blue-200",
            "hover:border-accent",
            "focus:border-accent",
            getTransitionTwClasses("all", 200)
        ),
    tooltip: (): string =>
        buildCssClassName(
            "!rounded-[5px]",
            "!opacity-[0.98]",
            "shadow-sm",
            "!border-[1px]",
            "after:!hidden",
            "before:!hidden",
            "!p-[5px]",
            "select-none"
        ),
    dropdown: {
        container: (): string => "relative",
        opener: (): string =>
            buildCssClassName(
                "flex",
                "items-center",
                "justify-center",
                "w-[35px]",
                "h-[35px]",
                "rounded-full",
                "group",
                "bg-blue-600",
                "!outline-none",
                "hover:bg-blue-500",
                "focus-visible:bg-blue-500",
                getTransitionTwClasses("all", 200)
            ),
        dots: (): string => "flex items-center gap-x-[3px]",
        dot: (): string =>
            buildCssClassName(
                "rounded-full",
                "w-[5px]",
                "h-[5px]",
                "bg-blue-200",
                "group-focus-visible:bg-accent",
                "group-hover:bg-accent",
                getTransitionTwClasses("all", 200)
            ),
        listWrapper: (): string => "fixed z-[9999] -translate-x-[50%]",
        triangleWrapper: (): string => "relative mx-auto w-[20px] h-[13px]",
        triangleOuter: (): string =>
            buildCssClassName(
                "absolute",
                "top-0",
                "left-[50%]",
                "-translate-x-[50%]",
                "w-0",
                "h-0",
                "border-l-[10px]",
                "border-l-transparent",
                "border-r-[10px]",
                "border-r-transparent",
                "border-b-[13px]",
                { "border-b-blue-500": import.meta.env.PROD },
                { "border-b-blue-300": import.meta.env.DEV }
            ),
        triangleInner: (): string =>
            buildCssClassName(
                "absolute",
                "top-[2px]",
                "left-[50%]",
                "-translate-x-[50%]",
                "w-0",
                "h-0",
                "border-l-[9px]",
                "border-l-transparent",
                "border-r-[9px]",
                "border-r-transparent",
                "border-b-[11px]",
                { "border-b-white": import.meta.env.PROD },
                { "border-b-blue-300": import.meta.env.DEV }
            ),
        list: (): string =>
            buildCssClassName(
                "w-[160px]",
                "p-[5px]",
                "bg-white",
                "border-[1px]",
                { "border-blue-500": import.meta.env.PROD },
                { "border-blue-300": import.meta.env.DEV },
                "rounded-[10px]",
                "flex",
                "flex-col",
                "origin-left"
            ),
        listItem: ({ isLast }: { isLast: boolean }): string =>
            buildCssClassName(!isLast && "border-b-[1px] border-blue-600"),
        listItemInner: (): string =>
            buildCssClassName(
                "py-[6px]",
                "px-[8px]",
                "cursor-pointer",
                "!outline-none",
                "bg-white",
                "hover:bg-blue-600",
                "focus-visible:bg-blue-600",
                getTransitionTwClasses("all", 200)
            )
    }
} as const
